import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { useRazorpay } from '@/hooks/useRazorpay';
import { useShiprocketCheckout } from '@/hooks/useShiprocketCheckout';
import { toast } from 'sonner';
import { Product } from '@/types';

interface ExpressCheckoutOptions {
  productId?: string;
  variantId?: string;
  quantity?: number;
  selectedPotType?: string;
  selectedPotColor?: string;
  selectedSize?: string;
  price?: number;
  productName?: string;
  productImage?: string;
  product?: Product;
}

export const useExpressCheckout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToCart, cartItems } = useCart();
  const { initiateExpressPayment, loading: razorpayLoading } = useRazorpay();
  const { initiateCheckout: initiateShiprocketCheckout, loading: shiprocketLoading } = useShiprocketCheckout();

  const handleRazorpayExpress = async (options?: ExpressCheckoutOptions) => {
    setIsLoading(true);
    
    try {
      // If product options provided, add to cart first
      if (options?.product && options?.price) {
        const variantInfo = options.selectedPotType && options.selectedPotColor && options.selectedSize
          ? {
              pot_type: options.selectedPotType,
              pot_color: options.selectedPotColor,
              size: options.selectedSize,
            }
          : undefined;

        addToCart(
          options.product,
          undefined,
          options.selectedSize,
          variantInfo,
          true, // silent - don't open cart drawer for express checkout
          options.quantity
        );
        
        // Wait a moment for cart to update then get fresh cart items
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Use current cart items (they will be updated after addToCart if we added something)
      const itemsForPayment = options?.product ? cartItems : cartItems;

      if (itemsForPayment.length === 0 && !options?.product) {
        toast.error('Cart is empty');
        setIsLoading(false);
        return;
      }

      // For single product buy now, create a simple items array with full variant info and image
      if (options?.product && options?.price) {
        const productImage = options.productImage || options.product.image_urls?.[0] || '';
        
        const singleItemPayment = [{
          id: options.product.id,
          name: options.product.name,
          price: options.price,
          quantity: options.quantity || 1,
          image: productImage,
          image_urls: options.product.image_urls,
          thumbnail: productImage ? { url: productImage, alt: options.product.name } : undefined,
          variantInfo: options.selectedPotType && options.selectedSize
            ? {
                pot_type: options.selectedPotType,
                pot_color: options.selectedPotColor || '',
                size: options.selectedSize,
              }
            : null,
        }];
        await initiateExpressPayment(singleItemPayment);
      } else {
        // Use existing cart items
        await initiateExpressPayment(cartItems);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to initiate express checkout');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShiprocketCheckout = async (options?: ExpressCheckoutOptions, event?: Event) => {
    setIsLoading(true);
    
    try {
      // Create a synthetic event if not provided
      const checkoutEvent = event || new Event('click');

      // If product options provided, build cart items directly for checkout
      // This avoids the React state timing issue where cartItems hasn't updated yet
      if (options?.product && options?.price) {
        const variantInfo = options.selectedPotType && options.selectedPotColor && options.selectedSize
          ? {
              pot_type: options.selectedPotType,
              pot_color: options.selectedPotColor,
              size: options.selectedSize,
            }
          : undefined;

        // Also add to cart for consistency (silent)
        addToCart(
          options.product,
          undefined,
          options.selectedSize,
          variantInfo,
          true,
          options.quantity
        );

        // Build direct cart items for Shiprocket (bypass stale state)
        const directCartItems = [{
          id: options.product.id,
          name: options.product.name,
          price: options.price,
          quantity: options.quantity || 1,
          variantInfo: variantInfo || null,
        }];

        // Initiate Shiprocket checkout with direct items
        await initiateShiprocketCheckout(checkoutEvent, directCartItems);
      } else {
        // Use existing cart items
        await initiateShiprocketCheckout(checkoutEvent);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to initiate Shiprocket checkout');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyNow = (options: ExpressCheckoutOptions, checkoutType: 'razorpay' | 'shiprocket', event?: Event) => {
    if (checkoutType === 'razorpay') {
      handleRazorpayExpress(options);
    } else {
      handleShiprocketCheckout(options, event);
    }
  };

  return {
    isLoading: isLoading || razorpayLoading || shiprocketLoading,
    handleRazorpayExpress,
    handleShiprocketCheckout,
    handleBuyNow,
  };
};
